<script setup lang="ts">
import type { AcceptableValue } from 'reka-ui';
import { computed, markRaw, onMounted, ref, type Component, watch } from 'vue';
import {
    PButton,
    PSelect,
    PSelectContent,
    PSelectItem,
    PSelectTrigger,
    PSelectValue,
    PToggleGroup,
    PToggleGroupItem,
} from '@pokerly/ui';
import { IconAdjustmentsHorizontal, IconCards, IconPlayCard2, IconShirt, IconMoodHappy } from '@tabler/icons-vue';
import type { VotingScaleSize } from '@pokerly/contracts';
import {
    VotingOption,
    VotingOptionBadge,
    VotingOptionDetail,
    VotingOptionIcon,
} from '@/components/game/voting-option/index.js';
import { useVotingScalesStore, type VotingScaleGroup } from '@/stores/voting-scales.ts';
import { useSessionStore } from '@/stores/session.ts';
import type { DraftVotingScalePreview } from './draft-voting-scale-preview.ts';

const emit = defineEmits<{
    'preview-change': [preview: DraftVotingScalePreview | null];
}>();

const votingScalesStore = useVotingScalesStore();
const sessionStore = useSessionStore();

const selectedGroupKey = ref<string>('');
const selectedVariantId = ref<string>('');
const selectedSize = ref<VotingScaleSize>('medium');
const isSubmitting = ref(false);

const groups = computed(() => votingScalesStore.groups);
const orderedGroups = computed(() => [...groups.value].sort(compareVotingScaleGroups));
const selectedGroup = computed(() => groups.value.find((group) => group.key === selectedGroupKey.value) ?? null);
const selectedVariant = computed(
    () => selectedGroup.value?.variants.find((variant) => variant.id === selectedVariantId.value) ?? null,
);
const variantOptions = computed(() => selectedGroup.value?.variants ?? []);
const requiresVariantSelection = computed(() => variantOptions.value.length > 1);
const variantSelectionOptions = computed(() => {
    return variantOptions.value.map((variant) => ({
        id: variant.id,
        label: (variant.options['medium'] ?? []).map((option) => option.label).join(', '),
    }));
});
const isSubmitDisabled = computed(() => !selectedVariant.value || isSubmitting.value || votingScalesStore.isLoading);

const groupIcons: Record<string, Component> = {
    fibonacci: markRaw(IconCards),
    't-shirt-sizes': markRaw(IconShirt),
    'powers-of-two': markRaw(IconPlayCard2),
    emoji: markRaw(IconMoodHappy),
    custom: markRaw(IconAdjustmentsHorizontal),
};

const scaleOptions = computed(() =>
    orderedGroups.value.map((group) => {
        const previewVariant =
            group.key === selectedGroupKey.value ? (selectedVariant.value ?? group.variants[0]) : group.variants[0];

        return {
            key: group.key,
            title: group.name,
            description: group.description ?? undefined,
            values: previewVariant?.options[selectedSize.value].map((option) => option.label) ?? [],
            icon: resolveGroupIcon(group),
        };
    }),
);

watch(
    orderedGroups,
    (value) => {
        if (!value.length) {
            selectedGroupKey.value = '';
            selectedVariantId.value = '';
            return;
        }

        const stillExists = value.some((group) => group.key === selectedGroupKey.value);
        if (!stillExists) {
            selectedGroupKey.value = value[0]?.key ?? '';
        }
    },
    { immediate: true },
);

watch(
    selectedGroup,
    (group) => {
        if (!group) {
            selectedVariantId.value = '';
            return;
        }

        const selectedStillExists = group.variants.some((variant) => variant.id === selectedVariantId.value);
        if (!selectedStillExists) {
            selectedVariantId.value = group.variants[0]?.id ?? '';
        }
    },
    { immediate: true },
);

watch(
    [selectedVariant, selectedSize],
    ([variant, size]) => {
        if (!variant) {
            emit('preview-change', null);
            return;
        }

        const options = variant.options[size];
        if (!options) {
            emit('preview-change', null);
            return;
        }
        emit('preview-change', {
            title: variant.variantKey ? `${variant.name} · ${variant.variantKey}` : variant.name,
            description: variant.description,
            size,
            options: variant.options[size].map((option) => option.label),
        });
    },
    { immediate: true },
);

onMounted(() => {
    void votingScalesStore.ensureLoaded();
});

function normalizeScaleIdentifier(value: string) {
    return value.toLowerCase().replace(/[_\s]+/g, '-');
}

function getVotingScalePriority(group: VotingScaleGroup) {
    const identifiers = [normalizeScaleIdentifier(group.key), normalizeScaleIdentifier(group.name)];

    if (identifiers.some((identifier) => identifier === 'fibonacci')) {
        return 0;
    }
    if (identifiers.some((identifier) => identifier.includes('t-shirt') || identifier.includes('shirt'))) {
        return 1;
    }
    if (identifiers.some((identifier) => identifier.includes('powers-of-two') || identifier.includes('power-of-two'))) {
        return 2;
    }
    if (identifiers.some((identifier) => identifier.includes('emoji'))) {
        return 3;
    }

    return Number.MAX_SAFE_INTEGER;
}

function compareVotingScaleGroups(left: VotingScaleGroup, right: VotingScaleGroup) {
    const priorityDifference = getVotingScalePriority(left) - getVotingScalePriority(right);
    if (priorityDifference !== 0) {
        return priorityDifference;
    }

    return left.name.localeCompare(right.name);
}

function resolveGroupIcon(group: VotingScaleGroup) {
    const normalizedKey = group.key.toLowerCase();
    const normalizedName = group.name.toLowerCase().replace(/\s+/g, '-');

    return groupIcons[normalizedKey] ?? groupIcons[normalizedName] ?? groupIcons.custom;
}

function selectGroup(groupKey: string) {
    selectedGroupKey.value = groupKey;
}

async function handleAssignScale() {
    if (!selectedVariant.value) {
        return;
    }
    isSubmitting.value = true;
    try {
        await sessionStore.assignVotingScale(selectedVariant.value.id, selectedSize.value);
    } finally {
        isSubmitting.value = false;
    }
}

function updateOptionSize(size: AcceptableValue | AcceptableValue[]) {
    if (!size) {
        return;
    }
    selectedSize.value = size as VotingScaleSize;
}
</script>

<template>
    <section
        class="mx-auto flex items-center w-full flex-col gap-6 rounded-2xl py-2 px-2"
        data-testid="session-voting-scale-selector"
    >
        <div class="p-2 text-center">
            <h2 class="font-bold text-foreground-primary">How your team will vote?</h2>
        </div>

        <div class="flex flex-col gap-8 py-4">
            <div class="flex flex-wrap justify-center gap-4" data-testid="voting-scale-group-options">
                <button
                    v-for="group in scaleOptions"
                    :key="group.key"
                    :data-testid="`voting-scale-group-option-${group.key}`"
                    type="button"
                    class="rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2"
                    @click="selectGroup(group.key)"
                >
                    <VotingOption :active="group.key === selectedGroupKey">
                        <VotingOptionBadge v-if="group.title === 'Fibonacci'">Most used</VotingOptionBadge>
                        <VotingOptionIcon>
                            <component :is="group.icon" />
                        </VotingOptionIcon>
                        <VotingOptionDetail
                            :title="group.title"
                            :values="group.values"
                            :description="group.description"
                        />
                    </VotingOption>
                </button>
            </div>
        </div>

        <div class="flex justify-center gap-4">
            <label
                v-if="requiresVariantSelection"
                class="flex flex-col gap-2 paragraph-sm font-medium text-foreground-primary"
            >
                Choose a scale
                <PSelect v-model="selectedVariantId">
                    <PSelectTrigger class="min-w-75 justify-between" aria-label="Story point estimate" size="lg">
                        <PSelectValue placeholder="Pick an estimate" />
                    </PSelectTrigger>
                    <PSelectContent>
                        <PSelectItem v-for="option in variantSelectionOptions" :key="option.id" :value="option.id">
                            <span>{{ option.label }}</span>
                        </PSelectItem>
                    </PSelectContent>
                </PSelect>
            </label>

            <label class="flex flex-col gap-2 paragraph-sm font-medium text-foreground-primary text-center">
                Scale Size
                <PToggleGroup
                    :model-value="selectedSize"
                    @update:model-value="updateOptionSize"
                    type="single"
                    variant="outline"
                    size="lg"
                    :spacing="0"
                >
                    <PToggleGroupItem value="small" :data-testid="'voting-scale-size-small'">Small</PToggleGroupItem>
                    <PToggleGroupItem value="medium" :data-testid="'voting-scale-size-medium'">Medium</PToggleGroupItem>
                    <PToggleGroupItem value="large" :data-testid="'voting-scale-size-large'">Large</PToggleGroupItem>
                </PToggleGroup>
            </label>
        </div>

        <div class="flex justify-end">
            <PButton :disabled="isSubmitDisabled" @click="handleAssignScale">
                {{ isSubmitting ? 'Starting session…' : 'Start session' }}
            </PButton>
        </div>
    </section>
</template>
